import { type SelectionsMachine } from '../../machine/selection-machine';
import { type SelectionEffectFactory, type SelectionEffect } from '../types';

/**
 * The Effects manager is a simple abstraction around the lifecycle and management of a SelectionEffect.
 * This also creates a single subscription to the machine whereas each SelectionEffect would normally
 * create and manage its own.
 */
export class EffectManager {
  #disposeMachineSubscriptions: (() => void) | null = null;
  #machine: SelectionsMachine;
  #effects: SelectionEffect[];

  constructor(machine: SelectionsMachine, factories: SelectionEffectFactory[]) {
    this.#machine = machine;
    this.#effects = factories.map((factory) => factory());
  }

  setup() {
    // Exit early if we've already setup the dispose method
    // so we don't possibly have memory leaks.
    if (this.#disposeMachineSubscriptions) {
      return;
    }

    for (const effect of this.#effects) {
      effect.setup?.();
    }

    this.#disposeMachineSubscriptions = this.#machine.subscribe((delta, state) => {
      // noop deltas are never sent to effects - exit early
      if (delta.kind === 'noop') {
        return;
      }

      for (const effect of this.#effects) {
        if (effect.deltaTypes.has(delta.kind)) {
          effect.onDelta(delta, state);
        }
      }
    });
  }

  dispose() {
    this.#disposeMachineSubscriptions?.();

    for (const effect of this.#effects) {
      effect.dispose?.();
    }
  }
}
